import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissoes, setPermissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      // Configurar axios imediatamente se houver token salvo
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
    return savedToken;
  });

  // Configurar axios para incluir token em todas as requisições
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/check`);
      setUser(response.data.user);
      setPermissoes(response.data.user.permissoes || []);
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      // Só fazer logout se o erro for 401 ou 403 (token inválido)
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password
      });

      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setPermissoes(userData.permissoes || []);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao fazer login';
      return { success: false, error: message };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await axios.post(`${API_URL}/auth/google`, { credential });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setPermissoes(userData.permissoes || []);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao fazer login com Google';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissoes([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasPermission = (rota) => {
    // Admin tem acesso total
    if (permissoes.includes('*')) return true;
    // Verifica rota exata
    if (permissoes.includes(rota)) return true;
    // Rotas de detalhe herdam da rota pai (ex: /pacientes/123 -> /pacientes)
    const rotaPai = '/' + rota.split('/').filter(Boolean)[0];
    if (rotaPai !== rota && permissoes.includes(rotaPai)) return true;
    return false;
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  const value = {
    user,
    permissoes,
    hasPermission,
    login,
    loginWithGoogle,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
